/**
 * Show Campaign Page
 * 
 * Displays marketing campaigns and special offers to customers.
 * This page loads campaign incentives and customer data from Firebase Functions
 * based on URL query parameters (account ID, customer ID, incentive set ID).
 * 
 * Features:
 * - Dynamic variable replacement for personalized customer incentives
 * - Direct HTTP requests to Firebase Functions
 * - Support for development environment with Firebase emulator
 * - Phone number formatting and click-to-call functionality
 * - Responsive layout for incentive display
 * 
 * URL Format:
 * /showCampaign?an=[accountNumber]&cp=[cellPhone]&cpni=[incentiveSetId]
 */
import { Component, Injector } from '@angular/core';
import type { OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule, Platform } from '@ionic/angular';
import { HttpClient } from '@angular/common/http';
import { Functions, getFunctions, httpsCallable, connectFunctionsEmulator } from '@angular/fire/functions';
import { firstValueFrom } from 'rxjs';

import { ViewBasePage } from '@shared/pages';
import { Constants } from '@shared/models';
import { CapacitorService } from '@shared/services';

// import { CallNumber } from 'capacitor-call-number';
// import { CampaignIncentiveSetsService } from '../../services/campaignIncentiveSets.service';
// import { FirestorePathsService } from '@shared/services';
// import { UsersService } from '@shared/services';
// import { UserAccountMapsService } from '@shared/services';
// import { CustomersService } from '@shared/servicese';
// import { CampaignIncentivesService } from '@services';;
// private campaignIncentivesService: CampaignIncentivesService,
// private customersService: CustomersService,
// private usersService: UsersService,
// private userAccountMapsService: UserAccountMapsService, private firestorePathsService: FirestorePathsService,
// private campaignIncentiveSetsService: CampaignIncentiveSetsService,
// private firebaseFunctionsService: FirebaseFunctionsService,

@Component({
    'selector': 'app-showCampaign',
    'templateUrl': './showCampaign.page.html',
    'styleUrls': ['./showCampaign.page.scss'],
    standalone: true,
    imports: [ CommonModule, FormsModule, IonicModule ]
})
export class ShowCampaignPage extends ViewBasePage implements OnInit {
    public myHTML = {};
    public vm: any = {};
    // private onDevice = false;
    // public phone  = 'tel:16618778631';
    public phone = '';
    private functions: Functions;

    constructor(
        private platform: Platform, 
        private httpClient: HttpClient,
        private capacitorService: CapacitorService, 
        injector: Injector
    ) {
        super(injector);
        super.setCurrentView(Constants.VIEW_SHOW_CAMPAIGN);
        // Initialize Firebase Functions
        this.functions = getFunctions();
        // this.onDevice = this.platform.is('cordova');
    }

    // http://localhost:8100/showCampaign?ai=4&ci=6&cpni=3
    // http://localhost:8100/showCampaign?ai=0&ci=48             cellPhone=9999993333
    // http://localhost:8100/showCampaign?an=00000-0000-00000&cuid=83Ror7zll1q3wU74sDG1
    // app.ShuttleNomix.com/showCampaign?an=00000-0000-00000&cuid=83Ror7zll1q3wU74sDG1
    // staging.ShuttleNomix.com/showCampaign?an=00000-0000-00000&cuid=83Ror7zll1q3wU74sDG1
    // localhost:8100/showCampaign?an=00000-0000-00000&cuid=83Ror7zll1q3wU74sDG1

    public override async ngOnInit(): Promise<void> {
        const domainInfo = this.utilsService.getDomainInfo();
        if (domainInfo.domainWithPort === 'localhost:8100') {
            // Connect to emulator when in development mode
            connectFunctionsEmulator(this.functions, 'localhost', 5001);
        }

        try {
            await this.uxHelperService.showSpinner('Loading data.');

            const params: any = this.getQueryParams();
            const accountNumber = params.accountNumber;
            const cellPhone = params.cellPhone;
            const incentiveSetId = params.incentiveSetId;

            const response = await this.loadData(accountNumber, cellPhone, incentiveSetId);
            if (!response.accountNumber || !response.customer || !response.incentiveSet || !response.incentives) {
                throw new Error('Invalid response from server.');
            }

            // Now merge customer data into the incentives for personalization
            await this.variableReplacementService.reset(accountNumber);
            this.variableReplacementService.customer = response.customer;
            const rawMessage = JSON.stringify(response.incentives);
            const renderedMessage = this.variableReplacementService.doVariableReplacements(rawMessage);

            this.vm.incentives = JSON.parse(renderedMessage);
            this.vm.customer = response.customer;
        } catch (err) {
            await this.uxHelperService.hideSpinner();
            const errorMessage = this.utilsService.extractError(err);
            this.uxHelperService.showAlert('Error', errorMessage);
        } finally {
            await this.uxHelperService.hideSpinner();
        }
    }

    private async loadData(accountNumber: string, cellPhone: string, incentiveSetId: string): Promise<any> {
        const prefix = `https://us-central1-shuttlenomix.cloudfunctions.net/`;
        const func = `getCampaign?`;
        const parms = `accountNumber=${accountNumber}&cellPhone=${cellPhone}&incentiveSetId=${incentiveSetId}`;
        const url = prefix + func + parms;

        // Using HttpClient for direct API call
        const response: any = await firstValueFrom(this.httpClient.get(url));
        return response;
    }

    private getQueryParams(): any {
        let accountNumber = '';
        let cellPhone = '';
        let incentiveSetId = '';
        const urlParams = new URLSearchParams(window.location.search);

        // Get the accountNumber from the query parameter.
        const aParmExists = urlParams.has('an');
        if (!aParmExists) {
            throw new Error('Query parameter an does not exist.');
        }
        accountNumber = urlParams.get('an');

        // Get the cellPhone from the query parameter.
        const cParmExists = urlParams.has('cp');
        if (!cParmExists) {
            throw new Error('Query parameter cp does not exist.');
        }
        cellPhone = urlParams.get('cp');

        // Get the incentiveSetId from the query parameter.
        const cpnParmExists = urlParams.has('cpni');
        if (!cpnParmExists) {
            throw new Error('Query parameter cpni does not exist.');
        }
        incentiveSetId = urlParams.get('cpni');
        return { accountNumber, cellPhone, incentiveSetId };
    }

    public formatPhoneNumber(phoneNumber): string {
        const formattedPhoneNumber = this.utilsService.formatPhoneNumberForDisplay(phoneNumber);
        return formattedPhoneNumber;
    }

    public doReplacements(txt: string): string {
        let str = '';

        str = txt.replace('[CustomerName]', this.vm.customer.customerName);
        // str = str.replace('[DealershipName]', this.vm.accountProfile.dealershipName);
        // str = str.replace('[DealershipPhoneNumber]', this.vm.accountProfile.dealershipPhoneNumber);

        return str;
    }


    public async onPhoneNumberClick() {
        // await this.capacitorService.callPrompt(this.vm.dealershipPhoneNumber, this.vm.dealershipName);
        await this.capacitorService.callPrompt('6618778631', 'Michael Carr');
    }
}

// this.vm = {
//     'title': 'Especially for [CustomerName].',
//     'subtitle': 'As a valuable customer of [DealershipName] we would like to say thank you and offer you these gifts.',
//     'customerName': 'Michael Carr',
//     'dealershipName': 'Frontier Toyota',
//     'dealershipPhoneNumber': '6618778631',
//     'incentives': [
//         {
//             'title': 'Car Wash',
//             'line1': 'Especially For [CustomerName]',
//             'line2': 'Normal Price: $20.00',
//             'line3': 'Discount: $20.00',
//             'line4': 'Your Price: $0.00',
//             'line5': 'Valid Dates: Oct 1 - Oct 30',
//             'line6': 'Incentive Code: CC-123456',
//             'line7': 'While Supplies Last [DealershipName]',
//             'buttonText': 'Click To Make An Appointment',
//             'showCallButton': true
//         },
//         {
//             'title': 'Tire Rotation',
//             'line1': 'For [CustomerName]',
//             'line2': 'Normal Price: $40.00',
//             'line3': 'Discount: 20%',
//             'line4': 'Your Price: $32.00',
//             'line5': 'Valid Dates: Nov 1 - Nov 30',
//             'line6': 'Incentive Code: CC-123457',
//             'line7': 'Get It Now',
//             'buttonText': 'Click To Make An Appointment',
//             'showCallButton': true
//         },
//         {
//             'title': 'Key Chain',
//             'line1': '',
//             'line2': 'Normal Price: $10.00',
//             'line3': 'Discount: $10.00',
//             'line4': 'Free',
//             'line5': 'Come on down while supplies last',
//             'line6': 'Valid Dates: Dec 1 - Dec 30',
//             'line7': 'Incentive Code: CC-123458',
//             'buttonText': 'Click To Make An Appointment',
//             'showCallButton': false
//         },
//         {
//             'title': '',
//             'line1': '',
//             'line2': '',
//             'line3': '',
//             'line4': '',
//             'line5': '',
//             'line6': '',
//             'line7': '',
//             'buttonText': '',
//             'showCallButton': false
//         }
//     ]
// };